import { cn } from '@/lib/utils';
import { SectionHeader } from '@/components/molecules/SectionHeader';
import type { Character } from '@/schemas/character';
import { STAT_IDS, scoreToModifier, type StatId } from '@/schemas/stats';

// page-specific (Fase 2 PR 8): step «+1 Caratteristica» del flusso di salita
// di livello. Pixel-parity dal mockup `.lvup-stats-grid` + `.lvup-stat-card`
// (styles.css mockup righe 1921-1985). Sostituisce il render shadcn-Card
// precedente.
//
// Le card sono `<button>` cliccabili. Stato `--picked` (selezionata) usa
// border-ink + bg paper-soft + barra-sinistra 4px ink + " ✓" dopo il nome
// + colore ink al valore "next". Stato `--cap` (stat già a 18) usa
// opacity 0.55 + cursor-not-allowed + label "Già a 18" rossa.
//
// Manuale V1 r. 1759: «scegli una Caratteristica e aumentala di 1». Cap 18.
// D-Q13 (SSOT): 6 card cliccabili (no dropdown, no drag&drop).
// D-Q14: se aumenta COS, l'HP delta è mostrato in tempo reale.
//
// **PR 8 cambio API**: i bottoni Indietro/Avanti sono ora delegati a
// `<StepNav>` esterno renderizzato da `LevelUpFlowPage`. Le prop `onNext`/
// `onBack` sono rimosse: lo step espone solo `onSelect`.

interface LevelUpStepStatProps {
  character: Character;
  selected: StatId | undefined;
  onSelect: (statId: StatId) => void;
  getStatCap: () => Record<StatId, boolean>;
}

const STAT_LABELS: Record<StatId, string> = {
  forza: 'Forza',
  destrezza: 'Destrezza',
  costituzione: 'Costituzione',
  intelligenza: 'Intelligenza',
  saggezza: 'Saggezza',
  carisma: 'Carisma',
};

export function LevelUpStepStat({
  character,
  selected,
  onSelect,
  getStatCap,
}: LevelUpStepStatProps) {
  const cap = getStatCap();
  return (
    <section>
      <SectionHeader
        title="Aumenta una Caratteristica di +1"
        sub="Manuale V1 r. 1759: scegli una delle sei Caratteristiche e aumentala di 1. Il massimo è 18; le Caratteristiche già a 18 sono disabilitate. Se aumenti Costituzione cresce anche il massimo PF."
      />
      <div className="mt-4 grid grid-cols-2 gap-[0.6rem] sm:grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
        {STAT_IDS.map((statId) => (
          <StatCard
            key={statId}
            statId={statId}
            character={character}
            selected={selected === statId}
            atCap={cap[statId]}
            onSelect={() => onSelect(statId)}
          />
        ))}
      </div>
    </section>
  );
}

interface StatCardProps {
  statId: StatId;
  character: Character;
  selected: boolean;
  atCap: boolean;
  onSelect: () => void;
}

function StatCard({ statId, character, selected, atCap, onSelect }: StatCardProps) {
  const score = character.stats[statId];
  const nextScore = Math.min(18, score + 1);
  return (
    <button
      type="button"
      disabled={atCap}
      onClick={onSelect}
      aria-pressed={selected}
      title={atCap ? 'Caratteristica già a 18 (massimo)' : ''}
      className={cn(
        'border-rule bg-paper-bright relative cursor-pointer border-[1.5px] px-4 py-[0.9rem] text-left transition-[border-color,background-color,transform] duration-150',
        'hover:not-disabled:border-ink hover:not-disabled:-translate-y-px',
        selected && 'border-ink bg-paper-soft',
        atCap && 'cursor-not-allowed opacity-55',
      )}
    >
      {selected ? (
        <span
          className="bg-ink pointer-events-none absolute top-0 bottom-0 left-0 w-[4px]"
          aria-hidden
        />
      ) : null}
      <StatCardName label={STAT_LABELS[statId]} selected={selected} />
      <StatCardRow score={score} nextScore={nextScore} selected={selected} />
      <StatCardMod score={score} nextScore={nextScore} />
      <StatCardFooter statId={statId} character={character} atCap={atCap} />
    </button>
  );
}

function StatCardName({ label, selected }: { label: string; selected: boolean }) {
  return (
    <div className="font-display text-ink mb-[0.4rem] text-[0.78rem] tracking-[0.1em] uppercase">
      {label}
      {selected ? <span className="text-ink font-bold"> ✓</span> : null}
    </div>
  );
}

function StatCardRow({
  score,
  nextScore,
  selected,
}: {
  score: number;
  nextScore: number;
  selected: boolean;
}) {
  return (
    <div className="font-display flex items-baseline gap-[0.45rem]">
      <span className="text-[1.3rem] opacity-70">{score}</span>
      <span className="text-[1.1rem] opacity-55">→</span>
      <span className={cn('text-[1.7rem] font-bold', selected && 'text-ink')}>{nextScore}</span>
    </div>
  );
}

function StatCardMod({ score, nextScore }: { score: number; nextScore: number }) {
  const mod = scoreToModifier(score);
  const nextMod = scoreToModifier(nextScore);
  const modChanges = mod !== nextMod;
  return (
    <div className="mt-[0.4rem] text-[0.82rem] opacity-85">
      Mod {formatMod(mod)}
      {modChanges ? (
        <span className="bg-ink/10 ml-1 px-[0.3em] font-semibold">→ {formatMod(nextMod)}</span>
      ) : null}
    </div>
  );
}

function StatCardFooter({
  statId,
  character,
  atCap,
}: {
  statId: StatId;
  character: Character;
  atCap: boolean;
}) {
  if (atCap) {
    return (
      <div className="font-display text-accent-dwr mt-[0.3rem] text-[0.65rem] tracking-[0.1em] uppercase">
        Già a 18
      </div>
    );
  }
  if (statId === 'costituzione') {
    return (
      <div className="text-ink-soft mt-[0.3rem] text-[0.78rem] italic">
        PF max {character.hp.max} → {character.hp.max + 1}
      </div>
    );
  }
  return null;
}

function formatMod(mod: number): string {
  return mod >= 0 ? `+${mod}` : `${mod}`;
}
